type DeltaOp = {
  insert?: unknown;
  attributes?: Record<string, any>;
};

type DeltaLike = {
  ops?: DeltaOp[];
};

import {
  ALIGN_SET,
  FONT_SIZE_SET,
  FONT_WEIGHT_SET,
  IMAGE_STYLE_CLASS_MAP,
  TEXT_COLOR_SET,
  BOOTSTRAP_TABLE_STYLES,
  parseTableRowId,
} from "./quill-config";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSafeLink(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  // Protocolos seguros
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^mailto:/i.test(trimmed)) return true;
  if (/^tel:/i.test(trimmed)) return true;
  // Aceptar links sin protocolo (ej: google.com, www.example.com)
  // Validar que parece ser un dominio o URL válida
  if (/^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}/i.test(trimmed)) return true;
  if (/^www\./i.test(trimmed)) return true;
  return false;
}

function classListToAttr(classes: string[]): string {
  const list = classes.filter(Boolean);
  return list.length ? ` class="${list.join(" ")}"` : "";
}

function buildInlineClasses(attrs: Record<string, any> | undefined): string[] {
  if (!attrs) return [];
  const classes: string[] = [];

  if (typeof attrs.color === "string" && attrs.color !== "no-color" && TEXT_COLOR_SET.has(attrs.color)) {
    classes.push(`text-${attrs.color}`);
  }

  if (typeof attrs.size === "string" && FONT_SIZE_SET.has(attrs.size)) {
    classes.push(`fs-${attrs.size}`);
  }

  if (typeof attrs.weight === "string" && FONT_WEIGHT_SET.has(attrs.weight)) {
    classes.push(`fw-${attrs.weight}`);
  }

  return classes;
}

function renderInline(text: string, attrs?: Record<string, any>): string {
  let html = escapeHtml(text);

  const classes = buildInlineClasses(attrs);
  if (classes.length) {
    html = `<span${classListToAttr(classes)}>${html}</span>`;
  }

  if (attrs?.bold) html = `<strong>${html}</strong>`;
  if (attrs?.italic) html = `<em>${html}</em>`;
  if (attrs?.underline) html = `<u>${html}</u>`;
  if (attrs?.strike) html = `<s>${html}</s>`;

  // Handle link attribute - can be string or object { href, target }
  if (attrs?.link) {
    let href: string;
    let target: string | undefined;
    if (typeof attrs.link === "string") {
      href = attrs.link.trim();
    } else if (typeof attrs.link === "object") {
      href = (attrs.link.href || "").trim();
      target = attrs.link.target;
    } else {
      href = "";
    }
    // Add https:// if link has no protocol
    if (href && !/^[a-z][a-z0-9+.-]*:/i.test(href)) {
      href = "https://" + href;
    }
    // Validate that link is safe before rendering
    if (href && isSafeLink(href)) {
      const safeHref = escapeHtml(href);
      const targetAttr = target === "_blank" ? ' target="_blank" rel="noopener noreferrer" aria-describedby="new_window_description"' : "";
      html = `<a href="${safeHref}"${targetAttr}>${html}</a>`;
    }
  }

  return html;
}

type Segment = {
  text: string;
  attrs?: Record<string, any>;
  isLineBreak?: boolean;
  imageSrc?: string;
};

type Block = {
  segments: Segment[];
  attrs?: Record<string, any>;
  isHorizontalRule?: boolean;
};

function parseBlocks(delta: DeltaLike): Block[] {
  const blocks: Block[] = [];
  let current: Block = { segments: [] };

  const ops = Array.isArray(delta.ops) ? delta.ops : [];
  for (const op of ops) {
    if (typeof op.insert === "object" && op.insert !== null && "hr" in op.insert) {
      if (current.segments.length > 0) {
        blocks.push(current);
        current = { segments: [] };
      }
      blocks.push({ segments: [], attrs: op.attributes, isHorizontalRule: true });
      continue;
    }

    // Handle linebreak embed (Shift+Enter inserts this)
    if (typeof op.insert === "object" && op.insert !== null && "linebreak" in op.insert) {
      current.segments.push({ text: "", isLineBreak: true });
      continue;
    }

    // Handle image embed inserted by Quill
    if (
      typeof op.insert === "object" &&
      op.insert !== null &&
      "image" in op.insert &&
      typeof (op.insert as any).image === "string"
    ) {
      current.segments.push({
        text: "",
        attrs: op.attributes,
        imageSrc: (op.insert as any).image,
      });
      continue;
    }

    if (typeof op.insert !== "string") {
      continue;
    }

    const text = op.insert;
    const parts = text.split("\n");
    parts.forEach((part, index) => {
      if (part) {
        current.segments.push({ text: part, attrs: op.attributes });
      }
      const isLineBreak = index < parts.length - 1;
      if (isLineBreak) {
        blocks.push({ ...current, attrs: op.attributes });
        current = { segments: [] };
      }
    });
  }

  if (current.segments.length > 0) {
    blocks.push(current);
  }

  return blocks;
}

function renderBlockContent(block: Block): string {
  return block.segments.length > 0
    ? block.segments.map((segment) =>
        segment.isLineBreak
          ? "<br>"
          : segment.imageSrc
          ? (() => {
              const attrs = segment.attrs ?? {};
              const styleClass =
                typeof attrs.imageStyle === "string" &&
                IMAGE_STYLE_CLASS_MAP[attrs.imageStyle]
                  ? IMAGE_STYLE_CLASS_MAP[attrs.imageStyle]
                  : "";
              const alt =
                typeof attrs.imageAlt === "string"
                  ? attrs.imageAlt
                  : typeof attrs.alt === "string"
                  ? attrs.alt
                  : "";
              const width =
                typeof attrs.imageWidth === "string"
                  ? attrs.imageWidth.trim()
                  : typeof attrs.width === "string"
                  ? attrs.width.trim()
                  : "";
              const height =
                typeof attrs.imageHeight === "string"
                  ? attrs.imageHeight.trim()
                  : typeof attrs.height === "string"
                  ? attrs.height.trim()
                  : "";
              const classAttr = styleClass
                ? ` class="${escapeHtml(styleClass)}"`
                : "";
              const altAttr = alt ? ` alt="${escapeHtml(alt)}"` : "";
              const widthAttr = width ? ` width="${escapeHtml(width)}"` : "";
              const heightAttr = height ? ` height="${escapeHtml(height)}"` : "";
              return `<img src="${escapeHtml(segment.imageSrc)}"${classAttr}${altAttr}${widthAttr}${heightAttr} />`;
            })()
          : renderInline(segment.text, segment.attrs)
      ).join("")
    : "";
}

function renderBlocks(blocks: Block[]): string {
  let html = "";
  let listType: "ordered" | "bullet" | null = null;

  const closeList = () => {
    if (!listType) return;
    html += listType === "ordered" ? "</ol>" : "</ul>";
    listType = null;
  };

  // Filter out empty blocks that are just spacing (no content, no special attributes)
  // Keep empty blocks only if they have special formatting (header, list, table, etc.)
  const filteredBlocks = blocks.filter((block) => {
    if (block.isHorizontalRule) return true;

    const hasContent = block.segments.length > 0;
    if (hasContent) return true;

    const blockAttrs = block.attrs ?? {};
    const hasSpecialFormatting =
      blockAttrs.header ||
      blockAttrs.list ||
      blockAttrs.align ||
      blockAttrs.table;

    if (hasSpecialFormatting) return true;
    return false;
  });

  let i = 0;
  while (i < filteredBlocks.length) {
    const block = filteredBlocks[i];
    const blockAttrs = block.attrs ?? {};

    if (block.isHorizontalRule) {
      closeList();
      html += "<hr>";
      i++;
      continue;
    }

    // Handle table blocks: group consecutive blocks with attrs.table into a <table>
    if (typeof blockAttrs.table === "string") {
      closeList();

      // Collect all consecutive table blocks
      const tableBlocks: Block[] = [];
      while (i < filteredBlocks.length && typeof (filteredBlocks[i].attrs?.table) === "string") {
        tableBlocks.push(filteredBlocks[i]);
        i++;
      }

      // Group by row ID (attrs.table value)
      const rows: Block[][] = [];
      let currentRowId: string | null = null;
      let currentRow: Block[] = [];
      for (const tb of tableBlocks) {
        const rowId = tb.attrs?.table as string;
        if (rowId !== currentRowId) {
          if (currentRow.length > 0) rows.push(currentRow);
          currentRow = [tb];
          currentRowId = rowId;
        } else {
          currentRow.push(tb);
        }
      }
      if (currentRow.length > 0) rows.push(currentRow);

      // Extract styles from first cell's row ID
      let tableStyles: string[] = [];
      if (rows.length > 0 && rows[0].length > 0) {
        const firstRowId = (rows[0][0].attrs?.table as string) || "";
        const parsed = parseTableRowId(firstRowId);
        tableStyles = parsed.styles;
      }

      // Build table classes
      const tableClasses = ["table"];
      for (const s of tableStyles) {
        const found = BOOTSTRAP_TABLE_STYLES.find((bs) => bs.key === s);
        if (found) tableClasses.push(found.cls);
      }

      html += `<div class="table-responsive py-4"><table class="${tableClasses.join(" ")}">`;

      // Separate header rows (hdr-) from body rows (row-)
      const headerRows: Block[][] = [];
      const bodyRows: Block[][] = [];
      for (const row of rows) {
        const rowId = (row[0].attrs?.table as string) || "";
        const parsed = parseTableRowId(rowId);
        if (parsed.isHeader) {
          headerRows.push(row);
        } else {
          bodyRows.push(row);
        }
      }

      // Render thead if there are header rows
      if (headerRows.length > 0) {
        html += "<thead>";
        for (const row of headerRows) {
          html += "<tr>";
          for (const cell of row) {
            const cellContent = renderBlockContent(cell) || "&nbsp;";
            html += `<th>${cellContent}</th>`;
          }
          html += "</tr>";
        }
        html += "</thead>";
      }

      // Render tbody
      if (bodyRows.length > 0) {
        html += "<tbody>";
        for (const row of bodyRows) {
          html += "<tr>";
          for (const cell of row) {
            const cellContent = renderBlockContent(cell) || "&nbsp;";
            html += `<td>${cellContent}</td>`;
          }
          html += "</tr>";
        }
        html += "</tbody>";
      }

      html += "</table></div>";
      continue;
    }

    const list = blockAttrs.list === "ordered" || blockAttrs.list === "bullet" ? blockAttrs.list : null;

    // Map Quill align values to Bootstrap classes
    let align = "";
    if (typeof blockAttrs.align === "string" && ALIGN_SET.has(blockAttrs.align)) {
      if (blockAttrs.align === "center") {
        align = "text-center";
      } else if (blockAttrs.align === "right") {
        align = "text-end";
      } else if (blockAttrs.align === "justify") {
        align = "text-justify";
      }
    }

    const content = renderBlockContent(block) || "<br/>";

    if (list) {
      if (listType !== list) {
        closeList();
        html += list === "ordered" ? "<ol>" : "<ul>";
        listType = list;
      }
      html += `<li${classListToAttr([align])}>${content}</li>`;
      i++;
      continue;
    }

    closeList();

    const headerLevel =
      typeof blockAttrs.header === "number" && blockAttrs.header >= 1 && blockAttrs.header <= 6
        ? blockAttrs.header
        : null;
    const tag = headerLevel ? `h${headerLevel}` : "p";
    html += `<${tag}${classListToAttr([align])}>${content}</${tag}>`;
    i++;
  }

  closeList();
  return html;
}

export function renderQuillDeltaToHtml(rawDelta: string | null | undefined): string {
  if (!rawDelta) return "";
  let parsed: DeltaLike | null = null;
  try {
    parsed = JSON.parse(rawDelta) as DeltaLike;
  } catch {
    return "";
  }
  if (!parsed || typeof parsed !== "object") return "";

  const blocks = parseBlocks(parsed);
  let html = renderBlocks(blocks);

  return html;
}
